import { Button, Form } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import {
  ReportProspectingMigratedWorkOrdersForm,
  ReportProspectingMigratedWorkOrdersParams,
} from '@/core/domain/reports/people/Report.types';
import { useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { FormatType } from '@/core/domain/reports/movements/abcCurve/abcCurve.types';
import { DateFormat } from '@/utils/moment';
import { useGenerateProspectingMigratedWorkOrdersReport } from '@/core/domain/reports/people/Report.hooks';
import ProspectingMigratedWorkOrdersForm from '@/components/CRM/ProspectingMigratedQuotes/ProspectingMigratedQuotesForm';

const ProspectingMigratedWorkOrders: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [form] = Form.useForm<ReportProspectingMigratedWorkOrdersForm>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const report = useReportGenerate();

  const generateProspectingMigratedWorkOrdersReport =
    useGenerateProspectingMigratedWorkOrdersReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();

      const obj: ReportProspectingMigratedWorkOrdersParams = {
        companyIds: values?.companyIds ? values?.companyIds.toString() : '',
        customerIds: values?.customerIds ? values?.customerIds.toString() : '',
        description: values?.description,
        endDate: values?.endDate
          ? values?.endDate.format(DateFormat)
          : undefined,
        startDate: values?.startDate
          ? values?.startDate.format(DateFormat)
          : undefined,
        finalOsKm: values?.finalOsKm,
        initialOsKm: values?.initialOsKm,
        showAllContacts: values?.showAllContacts,
        showOnlyMainContactsGroup: values?.showOnlyMainContactsGroup,
        showOnlyInsuredCustomers: values?.showOnlyInsuredCustomers,
        thirdParty: values?.thirdParty,
        workOrderStatus: values?.workOrderStatus,
        workOrderDate: values?.workOrderDate,
        workOrderType: values?.workOrderType,
        workOrderItemType: values.workOrderItemType,
      };

      const result =
        await generateProspectingMigratedWorkOrdersReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleCsv = () => {
    handleGenerateReport(FormatType.CSV, (result) =>
      report.downloadFile({
        result,
        fileName: 'prospeccao-orcamentos-migrados.csv',
      }),
    );
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <Button
            type="primary"
            block
            onClick={handleCsv}
            loading={fileLoading.CSV}
          >
            Gerar CSV
          </Button>
        }
      >
        <ProspectingMigratedWorkOrdersForm form={form} />
      </TabContentWrapper>
    </>
  );
};

export default ProspectingMigratedWorkOrders;
